import React, { useState, useEffect, useMemo } from 'react';
import { db, getCollectionName } from './firebase';
import { collection, onSnapshot, query, orderBy, where, deleteDoc, doc } from './firestore-compat';

export default function GestaoPassagens() {
  const [passagens, setPassagens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroFilial, setFiltroFilial] = useState('Todas');
  const [periodo, setPeriodo] = useState('mensal'); // 'semanal' ou 'mensal'
  const [filtroData, setFiltroData] = useState(new Date());

  useEffect(() => {
    const unsub = onSnapshot(query(collection(db, 'passagens_tecnicos'), orderBy('dataVisita', 'desc')), snap => {
      setPassagens(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return () => unsub();
  }, []);

  const handleDelete = async (id) => {
    if (window.confirm('⚠️ Tem certeza que deseja excluir este registro permanentemente?')) {
      try {
        await deleteDoc(doc(db, 'passagens_tecnicos', id));
      } catch (err) {
        console.error("Erro ao excluir:", err);
        alert("Erro ao excluir registro.");
      }
    }
  };

  const filteredData = useMemo(() => {
    return passagens.filter(p => {
      const pDate = new Date(p.dataVisita + 'T00:00:00');
      if (filtroFilial !== 'Todas' && p.filial !== filtroFilial) return false;
      if (periodo === 'mensal') {
        return pDate.getMonth() === filtroData.getMonth() && pDate.getFullYear() === filtroData.getFullYear();
      } else {
        const diffTime = Math.abs(filtroData - pDate);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays <= 7;
      }
    });
  }, [passagens, periodo, filtroData, filtroFilial]);

  const groupedData = useMemo(() => {
    const groups = {};
    filteredData.forEach(p => {
      if (!groups[p.dataVisita]) groups[p.dataVisita] = [];
      groups[p.dataVisita].push(p);
    });
    return Object.keys(groups).sort((a, b) => b.localeCompare(a)).map(date => ({
      date,
      items: groups[date]
    }));
  }, [filteredData]);

  const exportToCSV = () => {
    const headers = ["Data", "Filial", "Paciente", "Tempo", "Técnica", "Endereço", "Valor Passagem", "Transportes", "Almoço", "Total Geral"];
    const rows = filteredData.map(p => {
      const ida = p.transporteIda.slice(0, p.qtdTransportesIda).map(t => `${t.tipo}${t.linha ? ' ('+t.linha+')' : ''}`).join(' + ');
      const volta = p.transporteVolta.slice(0, p.qtdTransportesVolta).map(t => `${t.tipo}${t.linha ? ' ('+t.linha+')' : ''}`).join(' + ');
      
      return [
        new Date(p.dataVisita + 'T00:00:00').toLocaleDateString('pt-BR'),
        p.filial,
        p.pacienteNome,
        p.tempoTreinamento,
        p.tecnicaNome,
        p.enderecoPaciente,
        (p.totalPassagem || 0).toString().replace('.', ','),
        `IDA: ${ida} | VOLTA: ${volta}`,
        (p.valorAlimentacao || 25).toString().replace('.', ','),
        (p.totalGeral || (p.totalPassagem + (p.valorAlimentacao || 25))).toString().replace('.', ',')
      ];
    });

    const totalPassagem = filteredData.reduce((acc, p) => acc + (p.totalPassagem || 0), 0);
    const totalAlmoco = filteredData.reduce((acc, p) => acc + Number(p.valorAlimentacao || 25), 0);
    const totalGeral = filteredData.reduce((acc, p) => acc + (p.totalGeral || (p.totalPassagem + (p.valorAlimentacao || 25))), 0);
    
    rows.push(["TOTAL DO PERÍODO", "", "", "", "", "", (totalPassagem || 0).toString().replace('.', ','), "", (totalAlmoco || 0).toString().replace('.', ','), (totalGeral || 0).toString().replace('.', ',')]);

    const csvContent = "\uFEFF" + [headers, ...rows].map(e => e.map(cell => `"${cell}"`).join(";")).join("\n");
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement("a");
    link.setAttribute("href", URL.createObjectURL(blob));
    link.setAttribute("download", `gestao_passagens_${new Date().toLocaleDateString('pt-BR')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const s = {
    page: { padding: '24px', fontFamily: "'Inter', sans-serif", background: '#f4f7fa', minHeight: '100vh', color: '#000' },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', background: '#fff', padding: '20px 32px', borderRadius: '24px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' },
    title: { margin: 0, fontSize: '1.8rem', fontWeight: 800, color: '#000' },
    controls: { display: 'flex', gap: '12px', alignItems: 'center' },
    select: { padding: '10px 16px', borderRadius: '12px', background: '#fff', color: '#000', border: '1px solid #e2e8f0', fontWeight: 600, outline: 'none' },
    exportBtn: { background: '#10b981', color: '#fff', border: 'none', padding: '10px 20px', borderRadius: '12px', fontWeight: 800, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' },
    tableContainer: { padding: '0 4px' },
    table: { width: '100%', borderCollapse: 'separate', borderSpacing: '0 8px' },
    th: { textAlign: 'center', padding: '12px 8px', color: '#64748b', fontSize: '0.65rem', textTransform: 'uppercase', fontWeight: 900, letterSpacing: '0.05em' },
    dateGroup: { background: '#fff', fontWeight: 900, fontSize: '1rem', textAlign: 'center', border: '1px solid #e2e8f0', borderRadius: '16px', color: '#000', height: 'calc(100% - 4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', padding: '0 20px' },
    td: { padding: '6px 4px', fontSize: '0.82rem', color: '#000', border: 'none', textAlign: 'center', verticalAlign: 'middle', fontWeight: 500 },
    infoBox: { background: '#fff', padding: '12px 8px', borderRadius: '12px', border: '1px solid #e2e8f0', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '48px', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' },
    filialBox: (val) => ({ background: val === 'Homebaby' ? '#be185d' : '#1d4ed8', color: '#fff', padding: '12px 8px', borderRadius: '12px', fontWeight: 900, fontSize: '0.75rem', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '48px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' })
  };

  const formatCurrency = (val) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val);

  return (
    <div style={s.page}>
      <div style={s.header}>
        <h1 style={s.title}>📊 Gestão de Passagens</h1>
        <div style={s.controls}>
          <select style={s.select} value={filtroFilial} onChange={e => setFiltroFilial(e.target.value)}>
            <option value="Todas">Todas Filiais</option>
            <option value="Homebaby">Homebaby</option>
            <option value="Vitalcare">Vitalcare</option>
          </select>
          <select style={s.select} value={periodo} onChange={e => setPeriodo(e.target.value)}>
            <option value="mensal">Mensal</option>
            <option value="semanal">Semanal</option>
          </select>
          <input type="month" style={s.select} onChange={e => setFiltroData(new Date(e.target.value + '-01T00:00:00'))} />
          <button style={s.exportBtn} onClick={exportToCSV}>📥 Exportar Excel</button>
        </div>
      </div>

      <div style={s.tableContainer}>
        {loading ? <div style={{textAlign: 'center', padding: '40px'}}>Carregando registros...</div> : (
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>Data</th>
                <th style={s.th}>Filial</th>
                <th style={s.th}>Paciente</th>
                <th style={s.th}>Tempo</th>
                <th style={s.th}>Técnica</th>
                <th style={s.th}>Endereço</th>
                <th style={s.th}>Valor Passagem</th>
                <th style={s.th}>Transportes</th>
                <th style={s.th}>Almoço</th>
                <th style={s.th}>Total</th>
                <th style={s.th}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {groupedData.map(group => (
                <React.Fragment key={group.date}>
                  {group.items.map((p, idx) => (
                    <tr key={p.id}>
                      {idx === 0 && (
                        <td rowSpan={group.items.length} style={{...s.td, paddingRight: '12px', verticalAlign: 'stretch', height: '1px'}}>
                          <div style={{...s.dateGroup, height: '100%'}}>
                            {new Date(group.date + 'T00:00:00').toLocaleDateString('pt-BR')}
                          </div>
                        </td>
                      )}
                      <td style={s.td}>
                        <div style={s.filialBox(p.filial)}>
                          {p.filial === 'Homebaby' ? 'Homebaby' : 'Vitalcare'}
                        </div>
                      </td>
                      <td style={s.td}><div style={s.infoBox}><b>{p.pacienteNome}</b></div></td>
                      <td style={s.td}><div style={s.infoBox}>{p.tempoTreinamento}</div></td>
                      <td style={s.td}><div style={s.infoBox}>{p.tecnicaNome}</div></td>
                      <td style={s.td}><div style={{...s.infoBox, fontSize: '0.7rem', lineHeight: '1.2'}}>{p.enderecoPaciente}</div></td>
                      <td style={s.td}><div style={{...s.infoBox, fontWeight: 700}}>{formatCurrency(p.totalPassagem || 0)}</div></td>
                      <td style={s.td}>
                        <div style={{...s.infoBox, fontSize: '0.65rem', flexDirection: 'column', textAlign: 'left', alignItems: 'flex-start', padding: '8px'}}>
                          <span><b>Ida:</b> {p.transporteIda.slice(0, p.qtdTransportesIda).map(t => `${t.tipo}${t.linha ? ' ('+t.linha+')' : ''}`).join(' + ')}</span>
                          <span><b>Volta:</b> {p.transporteVolta.slice(0, p.qtdTransportesVolta).map(t => `${t.tipo}${t.linha ? ' ('+t.linha+')' : ''}`).join(' + ')}</span>
                        </div>
                      </td>
                      <td style={s.td}><div style={s.infoBox}>{formatCurrency(p.valorAlimentacao || 25)}</div></td>
                      <td style={s.td}><div style={{...s.infoBox, background: '#000', color: '#fff', fontWeight: 800}}>{formatCurrency(p.totalGeral || (p.totalPassagem + (p.valorAlimentacao || 25)))}</div></td>
                      <td style={s.td}>
                        <button 
                          onClick={() => handleDelete(p.id)}
                          style={{background: '#fee2e2', color: '#dc2626', border: 'none', padding: '12px', borderRadius: '12px', cursor: 'pointer', transition: 'all 0.2s'}}
                          onMouseEnter={e => e.currentTarget.style.background = '#fecaca'}
                          onMouseLeave={e => e.currentTarget.style.background = '#fee2e2'}
                          title="Excluir Registro"
                        >
                          🗑️
                        </button>
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
              
              {filteredData.length > 0 && (
                <tr>
                  <td colSpan="1" style={{background: 'transparent'}}></td>
                  <td colSpan="5" style={{...s.td, textAlign: 'right', paddingRight: '12px'}}>
                    <div style={{...s.infoBox, background: '#1e293b', color: '#fff', fontWeight: 900, justifyContent: 'flex-end', padding: '0 20px', border: 'none'}}>TOTAL DO PERÍODO:</div>
                  </td>
                  <td style={s.td}><div style={{...s.infoBox, background: '#1e293b', color: '#fff', fontWeight: 900, border: 'none'}}>{formatCurrency(filteredData.reduce((acc, p) => acc + (p.totalPassagem || 0), 0))}</div></td>
                  <td style={s.td}><div style={{...s.infoBox, background: '#1e293b', color: '#fff', border: 'none'}}></div></td>
                  <td style={s.td}><div style={{...s.infoBox, background: '#1e293b', color: '#fff', fontWeight: 900, border: 'none'}}>{formatCurrency(filteredData.reduce((acc, p) => acc + Number(p.valorAlimentacao || 25), 0))}</div></td>
                  <td style={s.td}><div style={{...s.infoBox, background: '#000', color: '#fff', fontWeight: 900, fontSize: '1rem', border: 'none'}}>{formatCurrency(filteredData.reduce((acc, p) => acc + (p.totalGeral || (p.totalPassagem + (p.valorAlimentacao || 25))), 0))}</div></td>
                  <td style={s.td}></td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
